import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { Button } from "../components/ui/button";
import { Plus, Eye, Edit3, Trash2, CheckCircle2, XCircle, Home as HomeIcon, RefreshCw, AlertCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { formatDA } from "../lib/i18n";

export default function Dashboard() {
  const { user, loading } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [renew, setRenew] = useState([]);

  useEffect(() => { if (!loading && !user) navigate("/login"); }, [user, loading, navigate]);
  useEffect(() => {
    if (user) {
      api.get("/me/properties").then((r) => setItems(r.data.items));
      api.get("/me/needs-renewal").then((r) => setRenew(r.data.items)).catch(() => {});
    }
  }, [user]);

  const renewProp = async (id) => {
    await api.patch(`/properties/${id}/renew`);
    setRenew((arr) => arr.filter((p) => p.id !== id));
  };

  const updateStatus = async (id, status) => {
    await api.patch(`/properties/${id}/status`, null, { params: { status } });
    setItems((arr) => arr.map((p) => p.id === id ? { ...p, status } : p));
  };
  const remove = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;
    await api.delete(`/properties/${id}`);
    setItems((arr) => arr.filter((p) => p.id !== id));
  };

  const statusColor = { active: "bg-emerald-50 text-emerald-700", sold: "bg-blue-50 text-blue-700", rented: "bg-purple-50 text-purple-700", inactive: "bg-slate-100 text-slate-600" };

  return (
    <div className="min-h-screen">
      <Header />
      <div className="pt-24 max-w-7xl mx-auto px-5 md:px-10 pb-20">
        <div className="flex items-end justify-between flex-wrap gap-4 mt-6 mb-10">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#111827]">{t("welcomeBack")}, {user?.name}</h1>
            <p className="text-slate-500 mt-2">{t("manageListings")}</p>
          </div>
          <Link to="/post"><Button className="bg-[#1B8F5A] hover:bg-[#157047]" data-testid="dash-add"><Plus size={16} className="mr-2" />{t("addListing")}</Button></Link>
        </div>

        {renew.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mb-8" data-testid="renew-banner">
            <div className="flex items-start gap-3">
              <AlertCircle size={22} className="text-amber-700 shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-bold text-amber-900">{t("renewBanner")}</h3>
                <p className="text-sm text-amber-800 mt-1">{t("renewSub")}</p>
                <div className="mt-4 space-y-2">
                  {renew.map((p) => (
                    <div key={p.id} className="flex items-center gap-3 bg-white p-3 rounded-xl">
                      <span className="text-xs font-bold text-slate-400">{p.reference}</span>
                      <span className="flex-1 text-sm font-medium text-[#111827] truncate">{p.title}</span>
                      <Button size="sm" onClick={() => renewProp(p.id)} className="bg-[#1B8F5A] hover:bg-[#157047]" data-testid={`renew-${p.id}`}>
                        <RefreshCw size={13} className="mr-1" />{t("renew")}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {items.length === 0 ? (
          <div className="bg-white rounded-2xl p-16 text-center border border-slate-200">
            <HomeIcon size={42} className="mx-auto text-slate-300" />
            <p className="text-slate-500 mt-4">{t("noListingsYet")}</p>
            <Link to="/post" className="inline-block mt-5"><Button className="bg-[#1B8F5A] hover:bg-[#157047]">{t("addListing")}</Button></Link>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="divide-y divide-slate-100">
              {items.map((p) => (
                <div key={p.id} className="p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid={`dash-row-${p.id}`}>
                  <img src={p.images?.[0] || "https://placehold.co/120x90/e5e7eb/9ca3af?text=Naydars"} alt="" className="w-full md:w-28 h-24 object-cover rounded-xl" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold text-slate-400">{p.reference}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded font-bold uppercase ${statusColor[p.status] || statusColor.inactive}`}>{t(`status${p.status?.[0]?.toUpperCase() + p.status?.slice(1)}`) || p.status}</span>
                    </div>
                    <Link to={`/property/${p.id}`} className="font-semibold text-[#111827] hover:text-[#1B8F5A] line-clamp-1">{p.title}</Link>
                    <div className="text-sm text-slate-500 mt-0.5">{p.city}, {p.wilaya}</div>
                    <div className="text-sm font-bold text-[#F59E0B] mt-1">{p.price_on_request ? t("priceOnRequest") : formatDA(p.price_da)}</div>
                    {p.views !== undefined && <div className="text-xs text-slate-400 mt-0.5">👁 {p.views} vues</div>}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Link to={`/property/${p.id}`}><Button size="sm" variant="outline"><Eye size={14} /></Button></Link>
                    <Link to={`/edit/${p.id}`}><Button size="sm" variant="outline" data-testid={`edit-${p.id}`}><Edit3 size={14} /></Button></Link>
                    {p.status === "active" && p.transaction === "sale" && (
                      <Button size="sm" variant="outline" onClick={() => updateStatus(p.id, "sold")} className="text-blue-700"><CheckCircle2 size={14} className="mr-1" />{t("markSold")}</Button>
                    )}
                    {p.status === "active" && p.transaction === "rent" && (
                      <Button size="sm" variant="outline" onClick={() => updateStatus(p.id, "rented")} className="text-purple-700"><CheckCircle2 size={14} className="mr-1" />{t("markRented")}</Button>
                    )}
                    {p.status === "active" ? (
                      <Button size="sm" variant="outline" onClick={() => updateStatus(p.id, "inactive")}><XCircle size={14} className="mr-1" />{t("deactivate")}</Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => updateStatus(p.id, "active")}>{t("activate")}</Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => remove(p.id)} className="text-red-600"><Trash2 size={14} /></Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
